import { computeRewardBonus } from "./bonuses";
import {
  applyTrustDelta,
  calculateContractScore,
  calculateFinalReward,
  getGrade,
} from "./scoring";
import type {
  Client,
  ClientId,
  Contract,
  Grade,
  Player,
  Reward,
  SaveGame,
} from "../types";

export const levelThresholds = [
  0, 100, 250, 450, 700, 1000, 1400, 1900, 2500, 3000,
] as const;

export const MAX_TABLE_LEVEL = levelThresholds.length;

export const DEFAULT_LEVEL_CAP = 10;

/** Neutral starting trust for a client without an explicit seed (0-100 scale). */
export const DEFAULT_CLIENT_TRUST = 50;

/** Trust at which a client starts paying the loyalty bonus (two S grades from the seed). */
export const TRUSTED_CLIENT_TRUST = 70;

/** Extra money a trusted client pays on top of the graded reward. */
export const TRUSTED_CLIENT_BONUS = 0.1;

/** Loyalty bonus for a client whose trust already meets {@link TRUSTED_CLIENT_TRUST}. */
export function trustedClientBonus(trust: number): number {
  return trust >= TRUSTED_CLIENT_TRUST ? TRUSTED_CLIENT_BONUS : 0;
}

/** Reward share paid for replaying an already completed contract. */
export const REPLAY_REWARD_MULTIPLIER = 0.25;

/**
 * Cumulative XP required to reach a given level (canon 04 §18.b). The first ten
 * levels come from the table; beyond it the curve grows quadratically.
 */
export function levelThreshold(level: number): number {
  if (level < 1) {
    return 0;
  }
  if (level <= MAX_TABLE_LEVEL) {
    return levelThresholds[level - 1] ?? 0;
  }
  const m = level - MAX_TABLE_LEVEL;
  return (levelThresholds[MAX_TABLE_LEVEL - 1] ?? 0) + 700 * m + 50 * m * (m + 1);
}

/**
 * Derives the player level from total XP, capped at levelCap.
 */
export function getPlayerLevel(xp: number, levelCap: number = DEFAULT_LEVEL_CAP): number {
  let level = 1;
  while (level < levelCap && xp >= levelThreshold(level + 1)) {
    level++;
  }
  return level;
}

export const BASE_REWARD_BY_DIFFICULTY: Record<number, Reward> = {
  1: { money: 650, xp: 70, reputation: 4 },
  2: { money: 1350, xp: 130, reputation: 9 },
  3: { money: 2650, xp: 215, reputation: 16 },
};

/**
 * Returns a fresh base reward for a difficulty tier (canon 11 §3), or a zero
 * reward when the tier is unknown.
 */
export function baseRewardForDifficulty(difficulty: number): Reward {
  const base = BASE_REWARD_BY_DIFFICULTY[difficulty];
  if (!base) {
    return { money: 0, xp: 0, reputation: 0 };
  }
  return { money: base.money, xp: base.xp, reputation: base.reputation };
}

/**
 * Whether a contract is unlocked for a player by reputation (canon 03 §6).
 */
export function isContractUnlocked(contract: Contract, player: Player): boolean {
  return player.reputation >= contract.requiredReputation;
}

/**
 * First unlocked, not-yet-completed contract in catalog order - the contract to
 * recommend on the Dashboard / offer as "next" on the Result screen.
 */
export function getRecommendedContract(
  contracts: readonly Contract[],
  player: Player,
): Contract | undefined {
  return contracts.find(
    (contract) =>
      isContractUnlocked(contract, player) && !player.completedContracts.includes(contract.id),
  );
}

/**
 * The next client to unlock: the locked client with the lowest reputation
 * requirement, or undefined when every client is already unlocked.
 */
export function getNextUnlockClient(
  clients: readonly Client[],
  unlockedClients: readonly ClientId[],
): Client | undefined {
  return [...clients]
    .filter((client) => !unlockedClients.includes(client.id))
    .sort((a, b) => (a.requiredReputation ?? 0) - (b.requiredReputation ?? 0))[0];
}

/**
 * Extends the current unlocked-client list with any clients whose reputation
 * threshold (canon 11 §5) is met, preserving order and avoiding duplicates.
 */
export function getUnlockedClients(
  clients: readonly Client[],
  reputation: number,
  current: readonly ClientId[],
): ClientId[] {
  const result = [...current];
  const seen = new Set(current);
  for (const client of clients) {
    if ((client.requiredReputation ?? 0) <= reputation && !seen.has(client.id)) {
      result.push(client.id);
      seen.add(client.id);
    }
  }
  return result;
}

export interface ContractRewardSummary {
  score: number;
  grade: Grade;
  reward: Reward;
  previousLevel: number;
  newLevel: number;
  leveledUp: boolean;
  skillPointsGained: number;
  urgentBonusPct: number;
  trustBonusPct: number;
  replay: boolean;
}

/**
 * Resolves a completed contract: scores it, grades it, scales the reward, and
 * returns a new save plus a summary. A client whose trust already meets
 * {@link TRUSTED_CLIENT_TRUST} pays {@link TRUSTED_CLIENT_BONUS} extra money;
 * a replayed contract neither earns nor loses trust (only first completions
 * shape the relationship). The input save is never mutated.
 */
export function applyContractRewards(
  save: SaveGame,
  contract: Contract,
  taskScores: readonly number[],
  clients: readonly Client[],
  urgentBonusPct = 0,
): { save: SaveGame; summary: ContractRewardSummary } {
  const score = calculateContractScore(taskScores);
  const grade = getGrade(score);
  const base = contract.reward ?? baseRewardForDifficulty(contract.difficulty);
  const { moneyBonus, xpBonus } = computeRewardBonus(save.player, grade);
  const replay = save.player.completedContracts.includes(contract.id);
  const replayScale = replay ? REPLAY_REWARD_MULTIPLIER : 1;
  const client = clients.find((entry) => entry.id === contract.clientId);
  const currentTrust =
    save.clientTrust[contract.clientId] ?? client?.trust ?? DEFAULT_CLIENT_TRUST;
  const trustBonusPct = trustedClientBonus(currentTrust);
  const reward: Reward = {
    money: Math.round(
      calculateFinalReward(base.money, grade, moneyBonus, 0) *
        (1 + urgentBonusPct + trustBonusPct) *
        replayScale,
    ),
    xp: Math.round(calculateFinalReward(base.xp, grade, xpBonus, 0) * replayScale),
    reputation: Math.round(
      calculateFinalReward(base.reputation, grade, 0, 0) * (1 + urgentBonusPct) * replayScale,
    ),
  };

  const newXp = save.player.xp + reward.xp;
  const newReputation = save.player.reputation + reward.reputation;
  const previousLevel = save.player.level;
  const newLevel = getPlayerLevel(newXp, DEFAULT_LEVEL_CAP);
  const skillPointsGained = Math.max(0, newLevel - previousLevel);
  const leveledUp = skillPointsGained > 0;
  const completedContracts = save.player.completedContracts.includes(contract.id)
    ? save.player.completedContracts
    : [...save.player.completedContracts, contract.id];

  const newPlayer: Player = {
    ...save.player,
    money: save.player.money + reward.money,
    xp: newXp,
    reputation: newReputation,
    level: newLevel,
    skillPoints: save.player.skillPoints + skillPointsGained,
    completedContracts,
  };

  const newTrust = replay ? currentTrust : applyTrustDelta(currentTrust, grade);

  const unlockedClients = getUnlockedClients(
    clients,
    newReputation,
    save.unlockedClients,
  );

  const previousUnlockedSet = new Set(save.unlockedClients);
  const newlyUnlockedEntries = unlockedClients
    .filter((id) => !previousUnlockedSet.has(id))
    .map((id) => [id, DEFAULT_CLIENT_TRUST] as const);

  const clientTrust = {
    ...save.clientTrust,
    ...Object.fromEntries(newlyUnlockedEntries),
    [contract.clientId]: newTrust,
  };

  const summary: ContractRewardSummary = {
    score,
    grade,
    reward,
    previousLevel,
    newLevel,
    leveledUp,
    skillPointsGained,
    urgentBonusPct,
    trustBonusPct,
    replay,
  };

  return {
    save: { ...save, player: newPlayer, clientTrust, unlockedClients },
    summary,
  };
}
